import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaFacebookF, FaLink, FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';
import {
  FiArrowRight,
  FiArrowUp,
  FiBookmark,
  FiBookOpen,
  FiCheck,
  FiChevronRight,
  FiChevronUp,
  FiClock,
  FiHeart,
  FiMail,
  FiTag,
} from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { trackGaEvent } from '../../utils/ga';
import { trackUserEvent } from '../../utils/reddit-pixel';
import { getShareBaseUrl } from '../../utils/share-url';
import LandingNavbar from '../landing/sections/Navbar';
import LearnFooter from './LearnFooter';
import './articles.css';
import { Field } from '../../components/a11y';
import { copyOrShow } from '../../utils/clipboard';
import { fireAndForget } from '../../utils/fireAndForget';

interface TocEntry {
  id: string;
  title: string;
}

interface Article {
  slug: string;
  title: string;
  subtitle?: string;
  category: string;
  reading_time: number;
  excerpt: string;
  published_at: string;
  updated_at?: string;
  author?: string;
  image?: string;
  html: string;
  toc: TocEntry[];
  keywords?: string[];
  /** Frontmatter `answer:`: a direct answer rendered under the H1. */
  answer?: string | null;
  /** Frontmatter `faq:`: rendered as "Common questions" and as FAQPage JSON-LD. */
  faq?: FaqEntry[];
}

interface FaqEntry {
  q: string;
  a: string;
}

interface RelatedArticle {
  slug: string;
  title: string;
  category: string;
  reading_time: number;
  excerpt: string;
}

const API_URL = import.meta.env.VITE_API_URL as string;

/* ────────────── Share buttons ────────────── */
function ShareButtons({ article, variant }: { article: Article; variant: 'header' | 'footer' }) {
  const [copied, setCopied] = useState(false);
  const articleUrl = `${getShareBaseUrl()}/learn/${article.slug}`;

  // Reddit pixel: track shares as a Custom event keyed on channel
  // (ShareArticle_X / _Facebook / _LinkedIn / _Copy). Uses trackUserEvent
  // so anonymous visitors fire but opted-out logged-in users do not —
  // virality signal Reddit's audience modeling weights highly. Fires on
  // click but does NOT preventDefault — the social network navigation
  // still happens normally in the new tab.
  const trackShare = useCallback(
    (channel: string) => {
      trackUserEvent('Custom', { customEventName: `ShareArticle_${channel}` });
      trackGaEvent('share', { method: channel, content_type: 'article', item_id: article.slug });
    },
    [article.slug],
  );

  const copyLink = useCallback(async () => {
    trackShare('Copy');
    // copyOrShow: the execCommand fallback where the Clipboard API is absent
    // or refuses, and a toast with the link when both fail, instead of the
    // bare writeText whose rejection was swallowed (sibling of #918).
    if (await copyOrShow(articleUrl, 'article link')) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [articleUrl, trackShare]);

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(articleUrl)}`;
  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`;
  const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`;

  if (variant === 'footer') {
    return (
      <div className="flex items-center gap-1.5">
        <a
          href={tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on X"
          onClick={() => trackShare('X')}
          className="w-9 h-9 flex items-center justify-center rounded-full text-(--ee-public-ink-40) hover:text-(--ee-on-fill) hover:bg-(--ee-brand-x) transition-all"
        >
          <FaXTwitter />
        </a>
        <a
          href={fbUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on Facebook"
          onClick={() => trackShare('Facebook')}
          className="w-9 h-9 flex items-center justify-center rounded-full text-(--ee-public-ink-40) hover:text-(--ee-on-fill) hover:bg-(--ee-brand-facebook) transition-all"
        >
          <FaFacebookF />
        </a>
        <a
          href={liUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share on LinkedIn"
          onClick={() => trackShare('LinkedIn')}
          className="w-9 h-9 flex items-center justify-center rounded-full text-(--ee-public-ink-40) hover:text-(--ee-on-fill) hover:bg-(--ee-brand-linkedin) transition-all"
        >
          <FaLinkedinIn />
        </a>
        <button
          onClick={fireAndForget(copyLink)}
          aria-label="Copy link"
          className="w-9 h-9 flex items-center justify-center rounded-full text-(--ee-public-ink-40) hover:text-primary hover:bg-primary/5 transition-all"
        >
          {copied ? <FiCheck /> : <FaLink />}
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 ml-auto">
      <a
        href={tweetUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
        onClick={() => trackShare('X')}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-(--ee-public-ink-30) hover:text-primary hover:bg-primary/5 transition-colors"
      >
        <FaXTwitter className="text-sm" />
      </a>
      <a
        href={fbUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
        onClick={() => trackShare('Facebook')}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-(--ee-public-ink-30) hover:text-primary hover:bg-primary/5 transition-colors"
      >
        <FaFacebookF className="text-sm" />
      </a>
      <a
        href={liUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
        onClick={() => trackShare('LinkedIn')}
        className="w-8 h-8 flex items-center justify-center rounded-lg text-(--ee-public-ink-30) hover:text-primary hover:bg-primary/5 transition-colors"
      >
        <FaLinkedinIn className="text-sm" />
      </a>
      <button
        onClick={fireAndForget(copyLink)}
        aria-label="Copy link"
        className="w-8 h-8 flex items-center justify-center rounded-lg text-(--ee-public-ink-30) hover:text-primary hover:bg-primary/5 transition-colors"
      >
        {copied ? <FiCheck className="text-sm" /> : <FaLink className="text-sm" />}
      </button>
    </div>
  );
}

/* ────────────── Newsletter ────────────── */
function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/articles/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="mt-14 p-6 sm:p-8 bg-base-200/30 rounded-2xl border border-base-200/80 text-center">
      {status === 'success' ? (
        <div className="flex items-center justify-center gap-2 text-(--ee-tw-green-600)">
          <FiCheck className="text-lg" />
          <p className="font-medium">
            You&apos;re subscribed! We&apos;ll send you new articles and tips.
          </p>
        </div>
      ) : (
        <>
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 mb-3">
            <FiMail className="text-primary" />
          </div>
          <h3 className="text-lg font-bold text-base-content">Get NVC tips in your inbox</h3>
          <p className="mt-1 text-sm text-base-content/55 max-w-md mx-auto">
            New articles and practical communication tips. No spam, unsubscribe anytime.
          </p>
          <form
            onSubmit={fireAndForget(handleSubmit)}
            className="mt-4 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
          >
            <Field
              label="Email address"
              variant="plain"
              labelClassName="sr-only"
              className="flex-1"
            >
              <input
                id="newsletter-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-2.5 rounded-lg border border-base-300 bg-base-100 text-sm focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/10"
              />
            </Field>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-(--ee-on-accent) text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          {status === 'error' && (
            <p className="mt-2 text-sm text-(--ee-tw-red-500)">
              Something went wrong. Please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Read the article that scripts/prerender.mjs baked into the static HTML
 * (as a JSON island in a `<script type="application/json">` tag). Returns
 * null on the server (no document) or when the script is missing/stale.
 *
 * This lets us resume from the prerendered content instead of showing
 * the "Failed to load article" state if the live API fetch fails — the
 * prerendered article body is already on the page; we just need the
 * structured shape back so React can keep rendering it.
 */
function readPrerenderedArticle(slug: string | undefined): Article | null {
  if (typeof document === 'undefined' || !slug) return null;
  const el = document.getElementById('__prerender_article__');
  if (!el?.textContent) return null;
  try {
    const data = JSON.parse(el.textContent);
    // Only trust the bootstrap if the slug matches — protects against
    // an SPA route change that didn't refresh the page (e.g., navigating
    // /learn/A → /learn/B inside the SPA carries the same DOM with a
    // stale prerender script from A).
    return data?.slug === slug ? (data as Article) : null;
  } catch {
    return null;
  }
}

/* ────────────── Main article page ────────────── */
export default function ArticlePage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { slug } = useParams<{ slug: string }>();
  const prerendered = useMemo(() => readPrerenderedArticle(slug), [slug]);
  const [article, setArticle] = useState<Article | null>(prerendered);
  const [related, setRelated] = useState<RelatedArticle[]>([]);
  // Skip the loading spinner if we already have a usable prerendered
  // article — the page is already painted with content, no need to
  // flash a spinner over it.
  const [loading, setLoading] = useState(!prerendered);
  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  // Synchronize state with slug DURING RENDER on SPA navigation. useState
  // initializers run only on mount, so without this a /learn/A → /learn/B
  // SPA route change would render A's content for one frame before the
  // useEffect cleared it. Pattern from the React docs ("Adjusting state
  // while rendering" — set state during render based on prop change).
  const [lastSlug, setLastSlug] = useState(slug);
  if (slug !== lastSlug) {
    setLastSlug(slug);
    setArticle(prerendered);
    setNotFound(false);
    setFetchError(false);
    setLoading(!prerendered);
  }
  const [tocCollapsed, setTocCollapsed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const articleRef = useRef<HTMLDivElement>(null);

  // Fetch article
  useEffect(() => {
    if (!slug) return;
    // Capture BEFORE any setState — used by all the resolve branches to
    // decide whether to flip to the error state or keep prerendered
    // content. A future edit must NOT move this below any setArticle call.
    const hadBootstrap = article?.slug === slug;

    // Stale-response guard. On /learn/foo → /learn/bar navigation while
    // foo's fetch is in flight, foo's resolved promise must NOT clobber
    // bar's state. Cleanup sets ignore=true so foo's .then() chain bails.
    let ignore = false;

    if (!hadBootstrap) {
      // The during-render slug-sync above already reset article/notFound/
      // fetchError/loading for SPA navigation. This branch handles the
      // remaining cases (e.g., initial mount with no prerender bootstrap).
      setRelated([]);
      window.scrollTo(0, 0);
    }

    fetch(`${API_URL}/articles/${slug}`)
      .then((r) => {
        if (ignore) return null;
        if (r.status === 404) {
          // 404 is authoritative — the article is gone. Show the "not
          // found" state even if we had a prerendered version (which is
          // now stale).
          setNotFound(true);
          setArticle(null);
          return null;
        }
        if (!r.ok) {
          // Non-404 HTTP error: keep prerendered content if we have it.
          // Only flip to the error state on a fresh visit with nothing
          // to fall back to.
          if (!hadBootstrap) setFetchError(true);
          return null;
        }
        return r.json();
      })
      .then((json) => {
        if (ignore || !json) return;
        if (json.success) setArticle(json.data);
        else if (!hadBootstrap) setNotFound(true);
      })
      .catch(() => {
        if (ignore) return;
        // Network error: same logic as non-404 HTTP error — preserve
        // the prerendered article if we have one. The user sees what
        // bots see; refresh will pick up new content when the API is
        // back.
        if (!hadBootstrap) setFetchError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
    // article.slug intentionally not a dep — we only re-fetch on slug
    // change from the route, not on every state update of `article`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Reddit pixel: signal "read an article" once per slug change. Uses
  // trackUserEvent so anonymous visitors fire but opted-out logged-in users
  // do not — articles are top-of-funnel SEO/Reddit-ads landing pages, so
  // this is the major content-engagement signal Reddit's audience modeling
  // wants. Fires after the article actually loads (not on the loading state).
  // Why: we want a fresh ViewContent event per slug only — including the full
  // `article` object would re-fire after refetch even though slug is the same.
  useEffect(() => {
    if (!article) return;
    trackUserEvent('ViewContent');
    // GA4: per-article view keyed on slug — the SEO/content signal (which
    // articles pull traffic, and which then convert).
    trackGaEvent('article_view', { item_id: article.slug });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [article?.slug]);

  // Fetch related articles
  useEffect(() => {
    if (!article) return;
    fetch(`${API_URL}/articles`)
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          const others = json.data.filter(
            (a: RelatedArticle & { slug: string }) =>
              a.slug !== article.slug && a.category === article.category,
          );
          setRelated(others.slice(0, 3));
        }
      })
      .catch(() => {});
  }, [article]);

  // Reading progress bar + back-to-top
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0);
        setShowBackToTop(scrollTop > 400);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [article]);

  // TOC active link tracking
  useEffect(() => {
    if (!article?.toc?.length) return;
    const headings = article.toc
      .map((t) => document.getElementById(t.id))
      .filter(Boolean) as HTMLElement[];
    const tocLinks = document.querySelectorAll('#toc a');

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        let current: HTMLElement | null = null;
        for (const h of headings) {
          if (h.getBoundingClientRect().top <= 100) current = h;
        }
        tocLinks.forEach((a) => {
          const href = a.getAttribute('href');
          a.classList.toggle('active', current ? href === `#${current.id}` : false);
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [article]);

  // Memoized JSON island for prerender hydration. MUST come before the
  // early-return guards below — hooks must be called in the same order
  // on every render (React error #310 otherwise). Articles are large
  // (~50-100KB); without useMemo we'd JSON.stringify + regex-replace
  // the body on every scroll-progress RAF tick.
  const articleBootstrap = useMemo(
    () => (article ? JSON.stringify(article).replace(/</g, '\\u003c') : null),
    [article],
  );

  // Only emit the JSON island during the prerender pass (headless
  // Chromium with the dedicated UA string). Live users — authenticated
  // or not — already have the article in React state by the time the
  // page is interactive, so the island would add ~25KB of payload with
  // zero benefit.
  const isPrerender =
    typeof navigator !== 'undefined' && navigator.userAgent?.includes('EmpathEasePrerender');

  if (loading) {
    return (
      <div className="min-h-screen bg-base-100 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-base-100">
        <Helmet>
          <title>Error - EmpathEase</title>
        </Helmet>
        <LandingNavbar />
        <div className="max-w-2xl mx-auto px-4 pt-32 text-center">
          <h1 className="text-3xl font-bold text-base-content mb-4">Failed to load article</h1>
          <p className="text-base-content/60 mb-8">Please check your connection and try again.</p>
          <Link to="/learn" className="btn btn-primary">
            Browse all articles
          </Link>
        </div>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-base-100">
        <Helmet>
          <title>Article not found - EmpathEase</title>
        </Helmet>
        <LandingNavbar />
        <div className="max-w-2xl mx-auto px-4 pt-32 text-center">
          <h1 className="text-3xl font-bold text-base-content mb-4">Article not found</h1>
          <p className="text-base-content/60 mb-8">
            This article doesn&apos;t exist or has been removed.
          </p>
          <Link to="/learn" className="btn btn-primary">
            Browse all articles
          </Link>
        </div>
      </div>
    );
  }

  const categoryLabel = article.category?.replace(/-/g, ' ') ?? '';
  const publishedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : null;
  const articleUrl = `${getShareBaseUrl()}/learn/${article.slug}`;

  // Strip HTML tags + collapse whitespace to a plain-text body for schema.
  // Schema.org articleBody accepts plain text or HTML; plain text avoids
  // the risk of the body being parsed by overly-eager AI extraction.
  //
  // Entity decode ordering: decode `&amp;` LAST. If we decoded `&amp;`
  // first, a double-encoded `&amp;lt;script&amp;gt;` would resolve to
  // `<script>` text inside articleBody (not an XSS — the JSON.stringify
  // → `<` escape on the script tag prevents breakout — but it does
  // ship malformed structured data to crawlers).
  const articleBodyText = article.html
    ? article.html
        // Strip comments first — otherwise commented-out HTML survives the
        // tag-strip step below and leaks into the schema.
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&#x27;/gi, "'")
        .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number.parseInt(n, 10)))
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    url: articleUrl,
    author: { '@type': 'Organization', name: article.author ?? 'EmpathEase' },
    publisher: {
      '@type': 'Organization',
      name: 'EmpathEase',
      logo: { '@type': 'ImageObject', url: `${getShareBaseUrl()}/logo.webp` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
    ...(article.published_at && { datePublished: article.published_at }),
    ...(article.updated_at && { dateModified: article.updated_at }),
    ...(article.image && { image: article.image }),
    ...(articleBodyText && { articleBody: articleBodyText }),
  };

  // The server drops malformed entries (ArticleService::normaliseFaq); this
  // guard keeps an older API or a hand-edited bootstrap from emitting a
  // Question with no answer.
  const faq = (Array.isArray(article.faq) ? article.faq : []).filter(
    (item): item is FaqEntry =>
      typeof item?.q === 'string' && item.q !== '' && typeof item?.a === 'string' && item.a !== '',
  );
  const answer =
    typeof article.answer === 'string' && article.answer.trim() !== '' ? article.answer : null;

  // FAQPage beside the Article JSON-LD, mirroring the landing page's
  // (pages/landing/index.tsx). Only when the article sets `faq:`.
  const faqData =
    faq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }
      : null;

  // BreadcrumbList for "Home > Learn > <Article Title>" — helps Google
  // display breadcrumbs in search results and gives AI engines a
  // clearer sense of where this article sits in the site hierarchy.
  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${getShareBaseUrl()}/` },
      { '@type': 'ListItem', position: 2, name: 'Learn', item: `${getShareBaseUrl()}/learn` },
      { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-base-100 flex flex-col" ref={articleRef}>
      {/* SEO */}
      <Helmet>
        <title>{`${article.title} - EmpathEase`}</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={articleUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:site_name" content="EmpathEase" />
        {/* Per-article OG card generated at build time by
            scripts/generate-og-images.mjs. Falls back to site-wide
            og-preview.webp if the build step is skipped or the slug
            file doesn't exist. */}
        <meta property="og:image" content={`${getShareBaseUrl()}/og/${article.slug}.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={`${getShareBaseUrl()}/og/${article.slug}.png`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData).replace(/</g, '\\u003c')}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbData).replace(/</g, '\\u003c')}
        </script>
        {faqData ? (
          <script type="application/ld+json">
            {JSON.stringify(faqData).replace(/</g, '\\u003c')}
          </script>
        ) : null}
        {/* Prerender hydration island — see articleBootstrap above.
            Only emitted during the prerender pass; live users have the
            article in React state already and don't need the duplicate. */}
        {isPrerender && articleBootstrap ? (
          <script
            type="application/json"
            id="__prerender_article__"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON island for client hydration; content escaped via JSON.stringify + `<` → `<`
            dangerouslySetInnerHTML={{ __html: articleBootstrap }}
          />
        ) : null}
      </Helmet>

      {/* Reading progress bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary to-secondary z-[60] transition-all duration-150"
        style={{ width: `${progress}%` }}
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Reading progress"
      />

      <LandingNavbar />

      <main className="flex-1 pt-16">
        {/* Article header */}
        <header className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-base-100 to-secondary/5" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

          <div className="relative max-w-[52rem] mx-auto px-4 sm:px-6 pt-12 pb-10 sm:pt-16 sm:pb-14">
            {/* Breadcrumb */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-(--ee-public-ink-50)">
                <li>
                  <Link to="/learn" className="hover:text-primary transition-colors">
                    Learn
                  </Link>
                </li>
                {article.category ? (
                  <>
                    <li>
                      <FiChevronRight className="text-[10px]" />
                    </li>
                    <li>
                      <Link
                        to={`/learn?category=${article.category}`}
                        className="hover:text-primary transition-colors capitalize"
                      >
                        {categoryLabel}
                      </Link>
                    </li>
                  </>
                ) : null}
              </ol>
            </nav>

            {/* Category badge */}
            {article.category ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider rounded-full mb-5">
                <FiBookmark className="text-[9px]" />
                <span className="capitalize">{categoryLabel}</span>
              </span>
            ) : null}

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-base-content leading-[1.15] tracking-tight">
              {article.title}
            </h1>

            {/* Direct answer (frontmatter `answer:`): the first paragraph under the H1. */}
            {answer ? (
              <p data-article-answer className="mt-5 text-lg text-base-content/80 leading-relaxed">
                {answer}
              </p>
            ) : null}

            {article.subtitle ? (
              <p className="mt-5 text-lg sm:text-xl text-base-content/60 leading-relaxed">
                {article.subtitle}
              </p>
            ) : null}

            {/* Meta bar */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-base-100 rounded-full border border-base-200 shadow-sm">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <img src="/logo.webp" alt="" className="w-5 h-5 rounded-full" />
                </div>
                <span className="text-sm font-medium text-base-content/70">
                  {article.author ?? 'EmpathEase'}
                </span>
              </div>

              {publishedDate ? (
                <span className="text-sm text-(--ee-public-ink-40)">{publishedDate}</span>
              ) : null}

              <span className="text-base-content/20">|</span>

              <span className="text-sm text-(--ee-public-ink-40) flex items-center gap-1">
                <FiClock className="text-[10px]" />
                {article.reading_time} min read
              </span>

              {/* Header share buttons */}
              <ShareButtons article={article} variant="header" />
            </div>
          </div>
        </header>

        {/* Body */}
        <div className="py-10 sm:py-14">
          <div className="max-w-[52rem] mx-auto px-4 sm:px-6">
            {/* TOC */}
            {article.toc && article.toc.length >= 3 ? (
              <nav
                id="toc"
                className={`mb-14 p-6 sm:p-8 bg-base-200/30 rounded-2xl border border-base-200/80 backdrop-blur-sm ${tocCollapsed ? 'toc-collapsed' : ''}`}
              >
                <button
                  onClick={() => setTocCollapsed((c) => !c)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h2 className="text-sm font-bold uppercase tracking-wider text-(--ee-public-ink-50) flex items-center gap-2">
                    In this article
                  </h2>
                  <FiChevronUp
                    className={`text-xs text-(--ee-public-ink-30) toc-chevron transition-transform ${tocCollapsed ? 'rotate-180' : ''}`}
                  />
                </button>
                <ol className="mt-4 space-y-0.5 toc-list">
                  {article.toc.map((item, i) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="flex items-center gap-3 py-2 px-2 -mx-2 rounded-lg text-[0.925rem] text-base-content/60 hover:text-primary hover:bg-primary/5 transition-all group"
                      >
                        <span className="shrink-0 w-6 h-6 rounded-md bg-primary/10 text-primary/60 text-[11px] font-bold flex items-center justify-center group-hover:bg-primary/20 group-hover:text-primary transition-colors">
                          {i + 1}
                        </span>
                        <span>{item.title}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            ) : null}

            {/* Article prose */}
            <div
              className="article-body prose prose-lg max-w-none
                prose-headings:text-base-content prose-headings:font-extrabold prose-headings:tracking-tight
                prose-h2:text-2xl prose-h2:sm:text-[1.75rem] prose-h2:mt-16 prose-h2:mb-5 prose-h2:scroll-mt-24 prose-h2:pb-3 prose-h2:border-b prose-h2:border-base-200/60
                prose-h3:text-xl prose-h3:mt-10 prose-h3:mb-3
                prose-p:text-base-content/75 prose-p:leading-[1.85] prose-p:text-[1.075rem]
                prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                prose-strong:text-base-content/90 prose-strong:font-bold
                prose-blockquote:border-l-4 prose-blockquote:border-primary/30 prose-blockquote:bg-primary/[0.03] prose-blockquote:rounded-r-xl prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:not-italic prose-blockquote:my-6
                prose-code:text-primary prose-code:bg-primary/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none prose-code:text-sm
                prose-li:text-base-content/75 prose-li:text-[1.075rem] prose-li:leading-[1.85]
                prose-ol:space-y-3 prose-ul:space-y-3
                prose-img:rounded-2xl prose-img:shadow-lg
                prose-hr:border-base-200"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.html) }}
            />

            {/* Common questions (frontmatter `faq:`), mirrored by the FAQPage JSON-LD. */}
            {faq.length > 0 ? (
              <section className="mt-16" aria-labelledby="common-questions">
                <h2
                  id="common-questions"
                  className="text-2xl sm:text-[1.75rem] font-extrabold tracking-tight text-base-content pb-3 mb-5 border-b border-base-200/60"
                >
                  Common questions
                </h2>
                <div className="space-y-6">
                  {faq.map((item, i) => (
                    // eslint-disable-next-line react/no-array-index-key -- Why: two FAQ entries may share a question, and the list comes from frontmatter and never reorders
                    <div key={i}>
                      <h3 className="text-lg font-bold text-base-content">{item.q}</h3>
                      <p className="mt-2 text-base-content/75 leading-[1.85] text-[1.075rem]">
                        {item.a}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {/* Soft mediation CTA — only for conflict/workplace articles */}
            {(article.category === 'conflict-resolution' || article.category === 'workplace') && (
              <div className="mt-12 p-6 sm:p-8 bg-base-200/40 rounded-2xl border border-base-200/80">
                <p className="text-base-content/70 text-[0.95rem] leading-relaxed">
                  If you&apos;re navigating a conflict like this with a partner, co-founder, or team
                  member, a structured conversation with a neutral facilitator can help clarify what
                  each person is actually trying to protect.
                </p>
                <Link
                  to="/conflict-resolution"
                  className="inline-flex items-center gap-1.5 mt-4 text-primary text-sm font-medium hover:underline"
                >
                  Learn about facilitated conflict resolution sessions{' '}
                  <FiArrowRight className="text-xs" />
                </Link>
              </div>
            )}

            {/* Inline CTA */}
            <div className="mt-16 relative overflow-hidden rounded-2xl border border-primary/20 shadow-xl shadow-primary/5">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-primary/[0.02] to-secondary/[0.06]" />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-secondary/10 rounded-full blur-2xl" />
              <div className="relative p-7 sm:p-9">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                        <FiHeart className="text-primary text-base" />
                      </div>
                      <h3 className="text-lg font-bold text-base-content">
                        Want to put this into practice?
                      </h3>
                    </div>
                    <p className="text-base-content/60 text-[0.925rem] leading-relaxed">
                      {isAuthenticated
                        ? 'Continue building real skills with interactive lessons and AI coaching.'
                        : 'Take our free 3-minute quiz to discover your relationship superpower, then build real skills with interactive lessons and AI coaching.'}
                    </p>
                  </div>
                  <Link
                    to={isAuthenticated ? '/app' : '/quiz-assessment'}
                    className="shrink-0 inline-flex items-center px-7 py-3.5 bg-primary hover:bg-primary/90 text-(--ee-on-accent) text-sm font-semibold rounded-xl transition-all shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 gap-2"
                  >
                    {isAuthenticated ? 'Go to Courses' : 'Take the Quiz'}{' '}
                    <FiArrowRight className="text-xs" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <NewsletterSignup />

            {/* Tags */}
            {article.keywords && article.keywords.length > 0 ? (
              <div className="mt-12 pt-8 border-t border-base-200">
                <p className="text-xs font-bold uppercase tracking-widest text-(--ee-public-ink-30) mb-4 flex items-center gap-2">
                  <FiTag className="text-[10px] text-primary/40" />
                  Topics
                </p>
                <div className="flex flex-wrap gap-2">
                  {article.keywords.map((kw) => (
                    <Link
                      key={kw}
                      to={`/learn?tag=${kw}`}
                      className="px-3.5 py-1.5 bg-base-200/50 text-base-content/55 text-sm rounded-full border border-base-200/80 hover:border-primary/30 hover:text-primary hover:bg-primary/5 transition-all"
                    >
                      {kw}
                    </Link>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Share bar (bottom) */}
            <div className="mt-10 pt-8 border-t border-base-200 flex items-center justify-between">
              <p className="text-sm font-medium text-(--ee-public-ink-40)">
                Found this helpful? Share it.
              </p>
              <ShareButtons article={article} variant="footer" />
            </div>
          </div>
        </div>
      </main>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="py-14 bg-base-200/30 border-t border-base-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <FiBookOpen className="text-primary text-sm" />
              </div>
              <h2 className="text-2xl font-bold text-base-content">Continue Reading</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link key={a.slug} to={`/learn/${a.slug}`} className="group">
                  <article className="bg-base-100 rounded-xl border border-base-200 p-6 hover:shadow-lg hover:border-primary/20 transition-all duration-300 h-full flex flex-col hover:-translate-y-0.5">
                    {a.category ? (
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary mb-2 capitalize">
                        {a.category.replace(/-/g, ' ')}
                      </span>
                    ) : null}
                    <h3 className="text-lg font-bold text-base-content group-hover:text-primary transition-colors leading-snug">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-base-content/55 text-sm leading-relaxed flex-1">
                      {a.excerpt}
                    </p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="text-xs text-(--ee-public-ink-35)">
                        {a.reading_time} min read
                      </span>
                      <span className="text-primary text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                        Read <FiArrowRight className="text-xs" />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="relative overflow-hidden py-20 border-t border-base-200">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-base-100 to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        <div className="relative max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-6">
            <FiHeart className="text-primary text-xl" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-base-content tracking-tight">
            Ready to transform your communication?
          </h2>
          <p className="mt-4 text-lg text-base-content/60 leading-relaxed">
            {isAuthenticated
              ? 'Continue your journey with interactive courses, AI coaching, and real conversations.'
              : 'Join thousands learning to communicate with compassion. Start with interactive courses, practice with AI coaching, and connect with real people.'}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {isAuthenticated ? (
              <Link
                to="/app"
                className="inline-flex items-center justify-center px-8 py-3.5 bg-primary hover:bg-primary/90 text-(--ee-on-accent) font-semibold rounded-xl transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 gap-2"
              >
                Go to Courses <FiArrowRight />
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center px-8 py-3.5 bg-primary hover:bg-primary/90 text-(--ee-on-accent) font-semibold rounded-xl transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 gap-2"
                >
                  Start Learning Free <FiArrowRight />
                </Link>
                <Link
                  to="/quiz-assessment"
                  className="inline-flex items-center justify-center px-8 py-3.5 bg-base-100 border border-base-300 hover:border-primary/30 text-base-content font-semibold rounded-xl transition-all hover:-translate-y-0.5"
                >
                  Take the Quiz
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <LearnFooter />

      {/* Back to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        className={`fixed bottom-6 right-6 w-10 h-10 rounded-full bg-base-100 border border-base-200 shadow-lg flex items-center justify-center text-(--ee-public-ink-40) hover:text-primary hover:border-primary/20 transition-all z-50 ${showBackToTop ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <FiArrowUp className="text-sm" />
      </button>
    </div>
  );
}
